import { computed, onMounted, onBeforeUnmount, ref, watch, getCurrentInstance, Ref, nextTick, MaybeRef, toValue, MaybeRefOrGetter, ComponentPublicInstance } from 'vue'
import { createEventHook } from '@vueuse/core'
import { Modal } from 'bootstrap'
import { useRouter } from 'vue-router'
import { chunk } from 'lodash'

export interface UnexpectedErrorEventArgs {
    error: unknown
}

export const unexpectedErrorEvent = createEventHook<UnexpectedErrorEventArgs>()

export interface PageTitleOptions {
    page: string
    item?: string | (() => string | undefined)
    itemOnly?: boolean
}

export function usePageTitle(page: string | (() => string), item?: string | (() => string | undefined), itemOnly = false) {

    const component = getCurrentInstance()
    const pageAndItem = computed(() => {
        const pageName = (typeof page === 'string') ? page : page?.()
        const itemName = (typeof item === 'string') ? item : item?.()
        return itemName ? (itemOnly ? itemName : `${pageName} - ${itemName}`) : pageName
    })

    const isMounted = ref(false)

    onMounted(() => {
        isMounted.value = true
    }, component)

    onBeforeUnmount(() => {
        // this keeps us from setting the title in async code that runs
        // after the page has been unmounted (so is no longer the current page)
        isMounted.value = false
        document.title = 'Tree Crossing · Share your family tree' // revert to default
    }, component)

    watch([pageAndItem, isMounted], () => {
        if (isMounted.value) {
            document.title = `${pageAndItem.value} · Tree Crossing`
        }
    })

    return pageAndItem
}

export function getSignInUrlWithRedirect(redirectUri: string) {
    return `/signin?redirect_uri=${encodeURIComponent(redirectUri)}`
}

export function getSafeRedirectUri() {
    // SECURITY: do not allow redirect to external sites
    const urlParams = new URLSearchParams(window.location.search)
    const uri = urlParams.get('redirect_uri')
    return uri?.startsWith("/") ? uri : undefined // must be a relative path
}

export function notImplemented() {
    alert("Sorry, this feature is not available yet.")
}

export function hideModalAsync(modalElem: HTMLElement) {
    const modal = Modal.getInstance(modalElem)

    if (modal) {
        return new Promise<void>((resolve) => {
            function onHidden() {
                modalElem.removeEventListener('hidden.bs.modal', onHidden)
                resolve()
            }
            modalElem.addEventListener('hidden.bs.modal', onHidden)
            modal.hide()
        })
    }
}

export function useModalAutoHide(modalRef: Ref<{ $el: HTMLElement } | HTMLElement | undefined>) {
    const router = useRouter()

    onMounted(() => {
        const removeGuard = router.beforeEach((to, from, next) => {
            const value = modalRef.value
            if (value) {
                const elem = '$el' in value ? value.$el as HTMLElement : value as HTMLElement
                Modal.getInstance(elem)?.hide()
            }
            next()
        })

        onBeforeUnmount(removeGuard)        
    })
}

export async function processResponsiveAsync<T>(items: T[], processItems: (batch: T[]) => void) {
    for (const batch of chunk(items, 100)) {
        const start = performance.now()
        processItems(batch)
        console.log(`ProcessResponsive: processed ${batch.length} items in ${(performance.now() - start).toFixed()}ms`)

        await new Promise(resolve => setTimeout(resolve, 0))
    }
}

export function reloadApp(path = '/home') {
    try {
        history.replaceState({}, '', path)
    } catch (e) { /* ignore */ }

    if (window.location.pathname == path) {
        window.location.reload() // set href to same path will not reload
    }
    else {
        window.location.replace(path)
    }
}

export type StatusStyle = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info'

export interface StatusInfo {
    text: string
    style: StatusStyle
}

export const PendingStatus: StatusInfo = {
    text: 'Pending',
    style: 'info',
}

export const LimitedStatus: StatusInfo = {
    text: 'Limited',
    style: 'secondary',
}
